import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { ArrowRight, Flame } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Hero = () => {
  const [content, setContent] = useState(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/homepage`);
      setContent(response.data.content);
    } catch (error) {
      console.error('Error fetching homepage content:', error);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (!content) {
    return null;
  }

  return (
    <section id="hero" className="relative min-h-[50vh] flex items-center justify-center overflow-hidden pt-20">
      {/* Background with subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-amber-50/30"></div>
      
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-amber-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-gray-200/30 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm border border-amber-200 rounded-full mb-4 shadow-sm">
            <Flame className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-gray-700">{content.heroBadgeText}</span>
          </div>

          {/* Main heading */}
          <h1 className="text-3xl md:text-5xl font-bold mb-3 leading-tight">
            <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-amber-800 bg-clip-text text-transparent">
              {content.heroTitle}
            </span>
            <br />
            <span className="text-gray-700">{content.heroSubtitle}</span>
          </h1>

          <p className="text-base md:text-lg text-gray-600 mb-6 max-w-3xl mx-auto leading-relaxed">
            {content.heroDescription}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
            <Button
              onClick={() => scrollToSection('products')}
              size="lg"
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-6 py-5 shadow-lg hover:shadow-xl transition-all duration-300 group"
            >
              {content.heroButton1Text}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={() => scrollToSection('contact')}
              size="lg"
              variant="outline"
              className="border-2 border-gray-300 hover:border-amber-600 hover:text-amber-700 px-6 py-5 transition-all duration-300"
            >
              {content.heroButton2Text}
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
            {content.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-xs md:text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};